import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Users, Trash2, Plus } from "lucide-react";

export default function StaffTab() {
  const [staff, setStaff] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const load = () => api.get("/farm/staff").then(r => setStaff(r.data)).catch(e => setError(e.response?.data?.detail || "Load error"));
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault(); setBusy(true); setError(""); setSuccess("");
    try {
      const { data } = await api.post("/farm/staff", form);
      setSuccess(`Invited ${data.email}. Share login: ${form.email} / ${form.password}`);
      setForm({ email: "", name: "", password: "" }); setShowAdd(false); await load();
    } catch (e) { setError(e.response?.data?.detail || "Invite failed"); } finally { setBusy(false); }
  };
  const remove = async (id) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Remove this staff member?")) return;
    setBusy(true); setError("");
    try { await api.delete(`/farm/staff/${id}`); await load(); } catch (e) { setError(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  if (!staff) return <div className="p-10 text-[var(--ff-text-muted)]" data-testid="staff-loading">Loading…</div>;
  return (
    <div className="space-y-6" data-testid="farm-staff-tab">
      <div className="flex items-end justify-between">
        <div>
          <h3 className="font-display text-2xl">Team ({staff.count})</h3>
          <p className="text-sm text-[var(--ff-text-muted)] mt-1">Staff can update fulfillment status — they cannot modify inventory or invite others.</p>
        </div>
        <button onClick={() => setShowAdd(v => !v)} className="btn-primary" data-testid="staff-add-toggle">
          <Plus size={16}/> {showAdd ? "Cancel" : "Add staff"}
        </button>
      </div>
      {error && <div className="bg-rose-50 border border-rose-200 p-3 rounded text-sm text-rose-800" data-testid="staff-error">{error}</div>}
      {success && <div className="bg-emerald-50 border border-emerald-200 p-3 rounded text-sm text-emerald-800" data-testid="staff-success">{success}</div>}
      {showAdd && (
        <form onSubmit={invite} className="card-flat p-5 grid md:grid-cols-3 gap-3" data-testid="staff-form">
          <input required type="email" placeholder="staff@email.com" className="input-base" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="staff-email"/>
          <input required placeholder="Full name" className="input-base" value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="staff-name"/>
          <input required minLength={6} placeholder="Initial password (6+ chars)" className="input-base" value={form.password} onChange={e => setForm({...form, password: e.target.value})} data-testid="staff-pwd"/>
          <button disabled={busy} type="submit" className="btn-primary md:col-span-3" data-testid="staff-invite-btn">{busy ? "Inviting…" : "Send invite"}</button>
        </form>
      )}
      <div className="card-flat divide-y" data-testid="staff-list">
        {staff.staff.map(s => (
          <div key={s.id} className="p-4 flex items-center gap-4" data-testid={`staff-row-${s.id}`}>
            <Users size={18} className="text-[var(--ff-text-muted)]"/>
            <div className="flex-1">
              <p className="font-medium text-sm">{s.name}</p>
              <p className="text-xs text-[var(--ff-text-muted)]">{s.email}</p>
            </div>
            <button onClick={() => remove(s.id)} className="text-rose-700 text-sm" data-testid={`staff-remove-${s.id}`}>
              <Trash2 size={14}/>
            </button>
          </div>
        ))}
        {staff.staff.length === 0 && <p className="p-6 text-sm text-[var(--ff-text-muted)]">No staff yet — invite a teammate above.</p>}
      </div>
    </div>
  );
}
